import React, { useEffect, useState } from "react";
import GridLayout from "react-grid-layout";
import { useNavigate } from "react-router-dom";
import BuilderRightSideBar from "../../../Layouts/BuilderRightSideBar";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import PageInfoAndActionBar from "../../../Layouts/PageInfoAndActionBar";
import AccessTemplateSelection from "../../../components/BuilderComponents/AccessTemplateSelection";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import NotesAndTags from "../../../components/BuilderComponents/NotesAndTags";
import Button from "../../../components/FormComponents/Button";
import Textarea from "../../../components/FormComponents/Textarea";
import { useAppBlocksContext } from "../../../context/AppBlocksContext";
import { useAuth0Context } from "../../../context/Auth0Context";
import { useTableContext } from "../../../context/TableContext";
import useElementDimensions from "../../../hooks/useElementDimensions";
import "./Chart.css";
import ChartEditSection from "./ChartEditSection";
import ChartWrapper from "./ChartWrapper";
import EditChartConfig from "./EditChartConfig";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import { generateObjectId } from "../../../utils/objectIdUtils";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import axiosInstance from "../../../services/axiosService";

const ChartRenderer = () => {
  const [{ width: elementWidth }, elementRef] = useElementDimensions();
  const [dragFromOutside, setDragFromOutside] = useState(false);

  const { layout, setLayout, layoutData, chartGlobalFilter, applicationBlockData } = useAppBlocksContext();
  const [allTableColumns, setAllTableColumns] = useState({});
  const [editRightsideBar, setEditRightsideBar] = useState({ id: "", show: false });
  const { getTableTypeData } = useTableContext();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const navigate = useNavigate();
  const { setSelectedAppBlock } = useAppBlocksContext();

  const allCharts = [
    "line/2d",
    "line/3d",
    "bar/2d",
    "bar/3d",
    "pie/2d",
    "table/2d",
    "table/3d",
    "spark_line/2d",
    "spark_area/2d",
    "spark_pie/2d",
    "spark_column/2d",
    "donut/2d"
  ];
  const getImagePath = (type) => {
    const [chartType] = type.split("/");
    if (chartType === "line") {
      return "stacked_line_chart";
    }
    if (chartType === "bar") {
      return "bar_chart";
    }
    if (chartType === "pie") {
      return "pie_chart";
    }
    if (chartType === "table") {
      return "table_chart";
    }
  };

  const getChartConfig = (type, id) => {
    const [chartType, visually] = type.split("/");
    return {
      id: id,
      type: visually,
      title: "",
      sub_title: "",
      chart_type: chartType,
      data_label: "",
      data_title: "",
      xaxis_title: "",
      yaxis_title: "",
      group_columns: [],
      aggregate_column: "",
      aggregate_function: "",
      axis_inverted: false,
      add_event_config: {},
      tile_aggregate_function: "",
      chart_styles: {}
    };
  };

  const fetchSingleTable = async () => {
    try {
      let { tableObject } = await getTableTypeData(
        applicationBlockData.table_type,
        applicationBlockData.config.table_id
      );
      setAllTableColumns(tableObject);
    } catch (error) {
      navigate("../");
      setSelectedAppBlock({ id: "", name: "", parentData: "", table_id: "", activity_id: "" });
    }
  };

  const onDrop = (elemParams, item, e) => {
    setDragFromOutside(false);
    let element = e.dataTransfer.getData("type");
    if (!item || !item.i) {
      return;
    }
    if (!element || element === "null") return;
    const elementObject = getChartConfig(element, item.i);
    layoutData.current[item.i] = elementObject;
    setLayout([item, ...layout]);
  };
  const handleLayoutChange = (list) => {
    if (dragFromOutside) return;
    setLayout(list);
  };
  const delay = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };
  const editChart = async (id) => {
    setEditRightsideBar({ show: false });
    await delay();
    const data = layoutData.current[id];
    setEditRightsideBar({ id: data.id, show: true, type: "chart", config: data });
  };

  const deleteChart = (id) => {
    const newArr = layout.filter((item) => item.i !== id);
    setLayout(newArr);
  };

  const handleDragStart = (e, type) => {
    setDragFromOutside(true);
    if (e.nativeEvent instanceof DragEvent) {
      e.dataTransfer.setData("type", type);
    }
  };

  const saveElement = (config) => {
    const newLayoutData = JSON.parse(JSON.stringify(layoutData.current));
    newLayoutData[config.id] = config;
    layoutData.current = newLayoutData;
    setLayout([...layout]);
  };

  const getChartLabel = (type) => {
    switch (type) {
      case "spark_line":
        return "Spark Line";
      case "spark_area":
        return "Spark Area";
      case "spark_pie":
        return "Spark Pie";
      case "spark_column":
        return "Spark Column";
      default:
        return type;
    }
  };

  useEffect(() => {
    if (!applicationBlockData.config.table_id) return;
    fetchSingleTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationBlockData.id]);

  return (
    <div className="chart_outer_body" style={{ gridTemplateColumns: "5fr 248px" }}>
      <div>
        <ChartBlockPageActionsHandler />
        <div className="chart_main_section page_main_section">
          <div
            style={{
              width: applicationBlockData.device === "mobile" ? "50%" : "100%"
            }}
            className={`charts_main_page border_radius_medium`}
          >
            <div className="d_flex gap_3 my_3 mx_0">
              <Button variant="secondary" className="float_btn" onClick={() => setIsEditOpen(!isEditOpen)}>
                Edit Global Filters
              </Button>
            </div>
            <div className="tile_cards">
              {chartGlobalFilter.tiles?.map((tile) => (
                <div key={tile.title} className="tile_single_card">
                  {tile.title}
                </div>
              ))}
            </div>
            <div className="builder_display_item_container">
              {chartGlobalFilter.global_filters.fields.map((filter) => (
                <React.Fragment key={filter.id}>
                  <div className="builder_display_items">{filter.custom_label || filter.display}</div>
                </React.Fragment>
              ))}
            </div>
            <div className="chart_droppable_container">
              <div className="chart_droppable_inside">
                <div ref={elementRef}></div>
                <GridLayout
                  rowHeight={110}
                  cols={12}
                  width={elementWidth}
                  layout={layout}
                  onDrop={(elemParams, item, e) => onDrop(elemParams, item, e)}
                  compactType={"vertical"}
                  isDroppable={true}
                  droppingItem={{ i: generateObjectId(), h: 4, w: 12 }}
                  useCSSTransforms={true}
                  onLayoutChange={handleLayoutChange}
                  className="draggable_elements_outer"
                >
                  {layout.map((item) => (
                    <div key={item.i} className="draggable_chart_fields">
                      <div className="chart_view">
                        <div className="edit_Del_buttons">
                          <ButtonActionIcon type="edit" size="sm" onClick={() => editChart(item.i)} />
                          <ButtonActionIcon type="delete" size="sm" onClick={() => deleteChart(item.i)} />
                          <h3>{layoutData.current[item.i].chart_type}</h3>
                        </div>
                        <div className="overlay_cover"></div>
                        {layoutData.current[item.i].chart_type === "table" ? (
                          <div>
                            <div>
                              <h1>{layoutData.current[item.i].title}</h1>
                            </div>
                            <div className="user_table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>{layoutData.current[item.i].xaxis_title}</th>
                                    <th>{layoutData.current[item.i].yaxis_title}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>-</td>
                                    <td>-</td>
                                  </tr>
                                  <tr>
                                    <td>-</td>
                                    <td>-</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <>
                            <ChartWrapper chartConfig={layoutData.current[item.i]} />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </GridLayout>
              </div>
            </div>
          </div>
        </div>
      </div>
      {editRightsideBar.show ? (
        <div className="sidebarView_edit_outer edit_open">
          <div className="sidebar_top_header">
            <span>Edit Chart</span>
            <MaterialIcon
              name="close"
              className="section_close_darkIcon"
              onClick={() => setEditRightsideBar({ show: false })}
            />
          </div>
          <div>
            {editRightsideBar.type === "chart" && (
              <EditChartConfig
                editRightsideBar={editRightsideBar}
                allTableColumns={allTableColumns}
                saveChartConfig={saveElement}
                tableId={applicationBlockData.config.table_id}
                activityId={applicationBlockData.config.activity_id || ""}
                onClose={() => setEditRightsideBar({ show: false })}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="rightsidebar">
          <div className="sidebar_top_header">Charts</div>
          <div className="right_sidebar_inner">
            {allCharts.map((type) => {
              const [chartType, visually] = type.split("/");

              const getImage = getImagePath(chartType);
              return (
                <div
                  key={type}
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, type)}
                  className="draggable_input_field"
                >
                  <MaterialIcon name={getImage} />
                  <span className="field_name">{`${getChartLabel(chartType)} ${visually === "3d" ? "-S" : ""}`}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <BuilderRightSideBar isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} heading="Edit Charts" size="large">
        <ChartEditSection
          setIsEditOpen={setIsEditOpen}
          allTableColumns={allTableColumns}
          tableId={applicationBlockData.config.table_id}
          activityId={applicationBlockData.config.activity_id || ""}
        />
      </BuilderRightSideBar>
    </div>
  );
};

const EditChartBlockStyles = () => {
  const { chartGlobalFilter, setChartGlobalFilter } = useAppBlocksContext();
  const [chartBlockStylesConfig, setChartBlockStylesConfig] = useState(
    JSON.stringify(chartGlobalFilter.styles || {}, null, 2)
  );
  const [jsonError, setJsonError] = useState("");

  const handleChangePivotConfig = (e) => {
    const value = e.target.value;
    setChartBlockStylesConfig(value);
    try {
      const parsedValue = JSON.parse(value);
      setChartGlobalFilter((prevLayout) => ({
        ...prevLayout,
        styles: parsedValue
      }));
      setJsonError("");
    } catch (error) {
      setJsonError(error.message);
    }
  };

  return (
    <div>
      <Textarea
        label="Enter chat block styles"
        value={chartBlockStylesConfig}
        onChange={handleChangePivotConfig}
        error={jsonError}
        rows={20}
      />
    </div>
  );
};

const ChartBlockPageActionsHandler = () => {
  const {
    layout,
    layoutData,
    chartGlobalFilter,
    applicationBlockData,
    setApplicationBlockData,
    handleSaveApplicationBlock,
    lockData,
    setLockData,
    handleCloneApplicationBlock
  } = useAppBlocksContext();
  const { user } = useAuth0Context();
  const initialModalState = {
    show: false,
    type: "",
    modalName: ""
  };
  const [isModalOpen, setIsModalOpen] = useState(initialModalState);

  const handleOpenModal = (type, modalName) => {
    setIsModalOpen({ show: true, type, modalName });
  };
  const handleCloseModal = () => {
    setIsModalOpen(initialModalState);
  };

  const handletoggleLock = async () => {
    try {
      if (lockData && lockData.user_id) {
        if (lockData.user_id !== user.sub) {
          toastError("Unauthorized");
          return;
        }
        const { message } = await axiosInstance.delete(`/app/lock/${applicationBlockData.id}`);
        toastSuccess(message);
        setLockData({});
      } else {
        const userinfo = `${user.first_name} (${user.email})`;
        const lock = {
          type: "chart",
          user_id: user.sub,
          user_info: userinfo,
          type_id: applicationBlockData.id,
          type_name: applicationBlockData.name
        };
        const { message } = await axiosInstance.post(`/app/lock`, lock);
        toastSuccess(message);
        setLockData(lock);
      }
    } catch (error) {
      console.error("API call failed", error);
    }
  };

  const handleAccessTemplate = (data) => {
    const copyBlockData = JSON.parse(JSON.stringify(applicationBlockData));
    copyBlockData.access_templates = data;
    setApplicationBlockData(copyBlockData);
    handleCloseModal();
  };

  const handleNotesAndTag = (data, keyName) => {
    const copyBlockData = JSON.parse(JSON.stringify(applicationBlockData));
    copyBlockData[keyName] = data;
    setApplicationBlockData(copyBlockData);
  };

  return (
    <>
      <PageInfoAndActionBar
        pagePath={`Blocks/Charts/${applicationBlockData.name}`}
        currentConfig={[layout, layoutData.current, chartGlobalFilter, applicationBlockData]}
        pageName="applicationblock"
        toggleLock={handletoggleLock}
        isLocked={lockData?.user_id || null}
        savePage={handleSaveApplicationBlock}
        clonePage={handleCloneApplicationBlock}
        notesPage={() => handleOpenModal("notes", "Enter Chart Note")}
        saveOwner={() => handleOpenModal("owner", "Assign Owner of Chart")}
        settingPage={() => handleOpenModal("style", "Chart Style")}
      />
      <BuilderModal isOpen={isModalOpen.show} onClose={handleCloseModal} modalName={isModalOpen.modalName}>
        {isModalOpen.type === "notes" && (
          <NotesAndTags
            notesValue={applicationBlockData.description || ""}
            tagValue={applicationBlockData.info_tag || []}
            onChange={handleNotesAndTag}
          />
        )}
        {isModalOpen.type === "owner" && (
          <AccessTemplateSelection
            templates={applicationBlockData.access_templates}
            handleChange={handleAccessTemplate}
          />
        )}
        {isModalOpen.type === "style" && <EditChartBlockStyles />}
      </BuilderModal>
    </>
  );
};

export default ChartRenderer;
export { ChartBlockPageActionsHandler };
